import { useState, useEffect } from 'react';
import { Image, RefreshCw, Download } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { TimestampDisplay } from './TimestampDisplay';
import { ScreenshotModal } from './ScreenshotModal';
import { CopyButton } from './CopyButton';
import { timecode, formatTimestamp } from '../lib/utils';
import type { ApiService } from '../lib/api';

interface ScreenshotPanelProps {
  api: ApiService | null;
  uuid: string;
  sn: string;
}

export const ScreenshotPanel = ({ api, uuid, sn }: ScreenshotPanelProps) => {
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastModified, setLastModified] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadScreenshot = async () => {
    if (!api || !uuid) return;
    setLoading(true);
    setError(null);
    try {
      const { blob, lastModified: lm } = await api.getDeviceScreenshot(uuid);
      if (blob.size > 0) {
        const url = URL.createObjectURL(blob);
        setScreenshot(url);
        setLastModified(lm);
      } else {
        setError('No screenshot available');
      }
    } catch (e) {
      setError((e as Error).message);
    }
    setLoading(false);
  };

  const downloadScreenshot = () => {
    if (!screenshot) return;
    const a = document.createElement('a');
    a.href = screenshot;
    a.download = `${sn || uuid.slice(0,8)}-${timecode(lastModified ? new Date(lastModified) : new Date())}.png`;
    a.click();
  };

  const requestScreenshot = async () => {
    if (!api || !uuid) return;
    setRequesting(true);
    try {
      await api.requestScreenshot(uuid);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
    setRequesting(false);
  };

  useEffect(() => { return () => { if (screenshot) URL.revokeObjectURL(screenshot); }; }, [screenshot]);
  useEffect(() => { loadScreenshot(); }, [api, uuid]);

  return (
    <Card title="Screenshot" icon={Image}>
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="secondary" onClick={loadScreenshot} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Load Latest
          </Button>
          <Button size="sm" variant="primary" onClick={requestScreenshot} disabled={requesting}>
            {requesting ? <RefreshCw size={14} className="animate-spin" /> : <Image size={14} />} Request New
          </Button>
        </div>

        {error && !error.includes('404') && <p className="text-red-400 text-sm">{error}</p>}
        {error && error.includes('404') && <p className="text-gray-500 text-sm py-2">No screenshot available. Requested a new screenshot.</p>}

        {screenshot && (
          <div className="space-y-2">
            <div
              className="bg-gray-900 rounded p-2 flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-blue-500/50 transition-all"
              onClick={() => setModalOpen(true)}
              title="Click to expand"
            >
              <img src={screenshot} alt="Device screenshot" className="max-w-full max-h-screen object-contain" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex">
                <TimestampDisplay
                  value={lastModified ? new Date(lastModified) : null}
                  className="text-xs text-gray-400"
                />
                <CopyButton value={formatTimestamp(lastModified).full} size={14} />
              </div>
              <Button size="sm" variant="success" onClick={downloadScreenshot}><Download size={14} /> Download</Button>
            </div>
          </div>
        )}
      </div>

      {modalOpen && screenshot && (
        <ScreenshotModal
          src={screenshot}
          alt={`Screenshot — ${sn || uuid}`}
          onClose={() => setModalOpen(false)}
          onDownload={downloadScreenshot}
        />
      )}
    </Card>
  );
};
